import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models for M201 (產品異動調整單)
// ============================================================

export interface FM110 {
  fm110_01: string;          // 異動單號 (PK)
  fm110_11: string | null;   // 異動日期 (ISO string)
  fm110_13: string;          // 庫位代號
  fm110_14: number;          // 明細筆數
  fm110_15: string;          // 建立人員
  fm110_16: string;          // 部門代號
  fm110_17: string;          // 異動類別
  fm110_18: string;          // +/- 方向
  fm110_21: boolean;         // 異常旗標
  fm110_22: string;          // 處理對策
  fm110_23: number;          // 調整率
  fm110_80: string;          // 核准狀態 N/P
  fm110_90: number;          // version
  fm110_91: string;          // modifier
  fm110_92: string | null;   // modify date
  fm110_93: string;          // modify note

  // Associations
  details?: FM111[];
}

export interface FM111 {
  fm111_01: string;          // 異動單號 FK
  fm111_02: number;          // 項次
  fm111_11: string;          // 異動類別
  fm111_13: string;          // +/- 方向
  fm111_14: string;          // 產品大類碼
  fm111_15: string;          // 產品細類碼
  fm111_16: string;          // 批號
  fm111_17: string;          // 單位
  fm111_18: number;          // 盤盈虧量
  fm111_19: string;          // 異動原因 code
  fm111_191: string;         // 異動原因說明
  fm111_20: string;          // 庫位
  fm111_21: string;          // 備用
  fm111_211: string;         // 異動說明
  fm111_22: boolean;         // 異動原因旗標
  fm111_23: number;          // 材積係數
  fm111_90: number;
  fm111_91: string;
  fm111_92: string | null;
  fm111_93: string;
}

// ============================================================
// Request / Response types
// ============================================================

export interface M201ListResponse {
  data: FM110[];
  total: number;
  page: number;
  size: number;
}

export interface M201CreateRequest {
  master: Partial<FM110>;
  details: Partial<FM111>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const m201Api = {
  async list(page = 1, size = 20): Promise<M201ListResponse> {
    const res = await apiClient.get('/api/m201', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FM110> {
    const res = await apiClient.get(`/api/m201/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: M201CreateRequest): Promise<FM110> {
    const res = await apiClient.post('/api/m201', data);
    return res.data;
  },

  async update(id: string, data: M201CreateRequest): Promise<FM110> {
    const res = await apiClient.put(`/api/m201/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/m201/${encodeURIComponent(id)}`, {
      data: { confirmed: true },
    });
  },

  // ── Approval workflow ──

  async submitForApproval(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/m201/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/m201/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/m201/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/m201/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/m201/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};

// ============================================================
// Approval types
// ============================================================

export interface ApprovalHistory {
  id: number;
  order_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}
